import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AdminUsersManagement } from "@/components/admin/AdminUsersManagement";
import { HealthAreasManagement } from "@/components/admin/HealthAreasManagement";

const Configuracoes = () => {
  const [loading, setLoading] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillStats, setBackfillStats] = useState<any>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  const saveConfig = async (scope: string, key: string, value: any) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from("config")
        .upsert({ scope, key, value: value }, { onConflict: "scope,key" });

      if (error) throw error;

      // Log audit
      await supabase.from("audit_logs").insert({
        action: "update",
        entity: "config",
        entity_id: null,
      });

      toast.success("Configuração salva com sucesso");
    } catch (error) {
      console.error("Erro ao salvar configuração:", error);
      toast.error("Erro ao salvar configuração");
    } finally {
      setLoading(false);
    }
  };

  const handleBackfillLabResults = async () => {
    try {
      setBackfillLoading(true);
      setBackfillStats(null);
      toast.info("Processando registros de exames...");

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Sessão não encontrada");
        return;
      }

      const { data, error } = await supabase.functions.invoke(
        "backfill-lab-results",
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        },
      );

      if (error) throw error;

      setBackfillStats(data.stats);

      if (data.stats.errors.length > 0) {
        toast.warning(
          `Processamento concluído com ${data.stats.errors.length} erros`,
        );
      } else {
        toast.success(
          `Processamento concluído! ${data.stats.insertedTests} testes inseridos.`,
        );
      }
    } catch (error) {
      console.error("Erro ao processar indicadores:", error);
      toast.error("Erro ao processar indicadores de saúde");
    } finally {
      setBackfillLoading(false);
    }
  };

  const testPagarmeConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus("idle");

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Sessão não encontrada");
        return;
      }

      // Tentar criar um cliente de teste (não vai realmente criar)
      const testData = {
        name: "Teste Conexão",
        email: "teste@miasalute.com.br",
        document: "00000000000",
      };

      const { error } = await supabase.functions.invoke(
        "pagarme-create-customer",
        {
          body: testData,
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        },
      );

      // Se não tiver erro de autenticação da API, conexão está OK
      if (!error || (error && !error.message?.includes("unauthorized"))) {
        setConnectionStatus("success");
        toast.success("Conexão com Pagar.me estabelecida!");
      } else {
        setConnectionStatus("error");
        toast.error("Erro na conexão com Pagar.me");
      }
    } catch (error) {
      console.error("Erro ao testar conexão:", error);
      setConnectionStatus("error");
      toast.error("Erro ao testar conexão");
    } finally {
      setTestingConnection(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Configurações</h1>
        <p className="text-muted-foreground">
          Gerencie as configurações globais do sistema
        </p>
      </div>

      <Tabs defaultValue="branding" className="space-y-4">
        <TabsList>
          <TabsTrigger value="branding">Branding</TabsTrigger>
          <TabsTrigger value="legal">Legal</TabsTrigger>
          <TabsTrigger value="email">E-mail</TabsTrigger>
          <TabsTrigger value="ia">IA</TabsTrigger>
          <TabsTrigger value="pagamentos">Pagamentos</TabsTrigger>
          <TabsTrigger value="processamento">Processamento</TabsTrigger>
          <TabsTrigger value="usuarios">Usuários</TabsTrigger>
          <TabsTrigger value="areas">Áreas da Saúde</TabsTrigger>
        </TabsList>

        <TabsContent value="branding" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Identidade Visual</CardTitle>
              <CardDescription>
                Logos, cores e elementos visuais da marca
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="logo-url">URL do Logo</Label>
                <Input id="logo-url" placeholder="https://..." />
              </div>
              <div className="space-y-2">
                <Label htmlFor="primary-color">Cor Primária</Label>
                <Input id="primary-color" type="color" />
              </div>
              <Button
                onClick={() => toast.info("Funcionalidade em desenvolvimento")}
              >
                Salvar
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="legal" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Documentos Legais</CardTitle>
              <CardDescription>
                URLs para Política de Privacidade e Termos de Uso
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="privacy-url">URL Política de Privacidade</Label>
                <Input id="privacy-url" placeholder="/politica-privacidade" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="terms-url">URL Termos de Uso</Label>
                <Input id="terms-url" placeholder="/termos-uso" />
              </div>
              <Button
                onClick={() => toast.info("Funcionalidade em desenvolvimento")}
              >
                Salvar
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="email" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configurações de E-mail</CardTitle>
              <CardDescription>Remetente e templates de e-mail</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sender-email">E-mail Remetente</Label>
                <Input
                  id="sender-email"
                  type="email"
                  placeholder="contato@miasalute.com.br"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="welcome-template">
                  Template de Boas-vindas
                </Label>
                <Textarea
                  id="welcome-template"
                  placeholder="Olá {{nome}}, bem-vindo..."
                  rows={5}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() =>
                    toast.info("Funcionalidade em desenvolvimento")
                  }
                >
                  Salvar
                </Button>
                <Button
                  variant="outline"
                  onClick={() => toast.info("E-mail de teste enviado")}
                >
                  Enviar Teste
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ia" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configurações de IA</CardTitle>
              <CardDescription>
                Modelo, thresholds e limites de processamento
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="model-version">Versão do Modelo</Label>
                <Input id="model-version" placeholder="gpt-4" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="summary-max-words">
                  Máximo de Palavras no Resumo
                </Label>
                <Input id="summary-max-words" type="number" placeholder="500" />
              </div>
              <Button
                onClick={() => toast.info("Funcionalidade em desenvolvimento")}
              >
                Salvar
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pagamentos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configurações de Pagamento</CardTitle>
              <CardDescription>
                Integração com Pagar.me - Secrets configurados via Lovable Cloud
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold">Status da Conexão</h4>
                    <p className="text-sm text-muted-foreground">
                      Testar conexão com a API do Pagar.me
                    </p>
                  </div>
                  {connectionStatus === "success" && (
                    <Badge variant="default" className="bg-green-600">
                      ✓ Conectado
                    </Badge>
                  )}
                  {connectionStatus === "error" && (
                    <Badge variant="destructive">✗ Erro</Badge>
                  )}
                </div>

                <Button
                  onClick={testPagarmeConnection}
                  disabled={testingConnection}
                  variant="outline"
                  className="w-full"
                >
                  {testingConnection ? "Testando..." : "Testar Conexão"}
                </Button>
              </div>

              <div className="space-y-2">
                <Label>Webhook URL</Label>
                <Input
                  value={`https://dgklrdcryunxiktufjde.supabase.co/functions/v1/pagarme-webhook`}
                  readOnly
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Configure esta URL no painel do Pagar.me com autenticação
                  Basic (Usuário e Senha do Webhook). Defina
                  PAGARME_WEBHOOK_USER e PAGARME_WEBHOOK_PASSWORD nas variáveis
                  de ambiente.
                </p>
              </div>

              <div className="rounded-lg bg-muted p-4 space-y-2">
                <h4 className="font-semibold text-sm">Secrets Configurados:</h4>
                <ul className="text-sm space-y-1">
                  <li className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    <span>PAGARME_API_KEY</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    <span>PAGARME_WEBHOOK_USER / PAGARME_WEBHOOK_PASSWORD</span>
                  </li>
                </ul>
                <p className="text-xs text-muted-foreground mt-2">
                  Secrets gerenciados via Lovable Cloud
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="processamento" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Processamento de Dados</CardTitle>
              <CardDescription>
                Processar registros existentes e gerar indicadores de saúde
                individuais
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Este processo irá analisar todos os registros de exames
                  existentes e criar indicadores individuais de saúde para cada
                  teste laboratorial encontrado.
                </p>
                <p className="text-sm text-muted-foreground">
                  Os indicadores gerados aparecerão nos cards de saúde dos
                  pacientes.
                </p>
              </div>

              <Button
                onClick={handleBackfillLabResults}
                disabled={backfillLoading}
              >
                {backfillLoading
                  ? "Processando..."
                  : "Gerar Indicadores de Saúde"}
              </Button>

              {backfillStats && (
                <div className="mt-4 p-4 border rounded-lg space-y-2">
                  <h3 className="font-semibold">
                    Resultados do Processamento:
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">
                        Total de registros:
                      </span>{" "}
                      <strong>{backfillStats.totalRecords}</strong>
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        Registros processados:
                      </span>{" "}
                      <strong>{backfillStats.processedRecords}</strong>
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        Total de testes:
                      </span>{" "}
                      <strong>{backfillStats.totalTests}</strong>
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        Testes inseridos:
                      </span>{" "}
                      <strong className="text-green-600">
                        {backfillStats.insertedTests}
                      </strong>
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        Registros pulados:
                      </span>{" "}
                      <strong>{backfillStats.skippedRecords}</strong>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Erros:</span>{" "}
                      <strong className="text-red-600">
                        {backfillStats.errors.length}
                      </strong>
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">
                        Tempo de processamento:
                      </span>{" "}
                      <strong>
                        {(backfillStats.processingTimeMs / 1000).toFixed(2)}s
                      </strong>
                    </div>
                  </div>

                  {backfillStats.errors.length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-semibold text-sm text-red-600 mb-2">
                        Erros encontrados:
                      </h4>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {backfillStats.errors.map((err: any, idx: number) => (
                          <div
                            key={idx}
                            className="text-xs text-muted-foreground"
                          >
                            <span className="font-mono">{err.recordId}</span>:{" "}
                            {err.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="usuarios">
          <AdminUsersManagement />
        </TabsContent>

        <TabsContent value="areas">
          <HealthAreasManagement />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Configuracoes;
